import { Link } from "react-router-dom";
import { HeartPulse, Trophy, ArrowRight, MapPin } from "lucide-react";
import { useI18n } from "@/i18n";
import { pickLang } from "@/components/panel/labels";
import { P2 } from "@/components/panel/labels2";

export function scoreColor(s) {
  return s >= 80 ? "#22c55e" : s >= 60 ? "#3b82f6" : s >= 40 ? "#f59e0b" : "#ef4444";
}

export function HealthRing({ score, size = 140, stroke = 12 }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const color = scoreColor(score ?? 0);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" data-testid="health-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-border" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * (score ?? 0)) / 100} transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.22,.9,.3,1)" }} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="fill-foreground font-head" style={{ fontSize: size * 0.3, fontWeight: 800 }}>{score ?? "—"}</text>
    </svg>
  );
}

export function HealthScore({ health, compact = false }) {
  const { lang, lp } = useI18n();
  const p = (k) => pickLang(P2[k], lang);
  if (!health) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="health-empty">
        {p("tip_more_tests")} <Link to={lp("/test")} className="font-600 text-primary underline">→</Link>
      </div>
    );
  }
  const s = health.score;
  const verdict = s >= 80 ? p("hGreat") : s >= 60 ? p("hGood") : s >= 40 ? p("hFair") : p("hPoor");
  const comps = [["consistency", "cConsistency"], ["ping", "cPing"], ["jitter", "cJitter"], ["bufferbloat", "cBloat"]];
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="health-score">
      <div className="flex flex-wrap items-center gap-6">
        <HealthRing score={s} size={compact ? 110 : 150} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs font-800 uppercase tracking-wider text-muted-foreground"><HeartPulse className="h-4 w-4 text-primary" /> {p("health")}</div>
          <div className="mt-1 font-head text-2xl font-800" style={{ color: scoreColor(s) }} data-testid="health-verdict">{verdict}</div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {comps.map(([k, lk]) => {
              const v = health.components[k];
              return (
                <div key={k} data-testid={`health-comp-${k}`}>
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">{p(lk)}</span><span className="font-mono font-700">{v == null ? p("noData") : v}</span></div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${v ?? 0}%`, background: scoreColor(v ?? 0) }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {!compact && health.tips?.length > 0 && (
        <div className="mt-6 rounded-xl bg-secondary/60 p-4" data-testid="health-tips">
          <div className="text-xs font-800 uppercase tracking-wider text-muted-foreground">{p("tipsTitle")}</div>
          <ul className="mt-2 space-y-1.5 text-sm">
            {health.tips.map((tk) => <li key={tk} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{p(`tip_${tk}`)}</li>)}
          </ul>
          {health.tips.includes("run_bufferbloat") && <Link to={lp("/bufferbloat")} className="mt-3 inline-flex items-center gap-1 text-sm font-600 text-primary">{p("cBloat")} <ArrowRight className="h-4 w-4" /></Link>}
        </div>
      )}
    </div>
  );
}

export function AreaCompare({ area }) {
  const { lang, lp } = useI18n();
  const p = (k) => pickLang(P2[k], lang);
  if (!area) return <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="area-empty">{p("noArea")}</div>;
  const fill = (s) => s.replace("{isp}", area.isp).replace("{city}", area.city).replace("{p}", area.percentile);
  const diff = area.isp_avg ? Math.round(((area.my_avg - area.isp_avg) / area.isp_avg) * 100) : null;
  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm" data-testid="area-compare">
      <div className="flex items-center gap-2 text-xs font-800 uppercase tracking-wider text-muted-foreground"><MapPin className="h-4 w-4 text-primary" /> {p("area")}</div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="text-xs text-muted-foreground">{p("yourAvg")}</div>
          <div className="mt-1 font-head text-3xl font-800 text-primary">{area.my_avg} <span className="text-sm font-600 text-muted-foreground">Mbps</span></div>
        </div>
        <div className="rounded-xl border border-border p-4">
          <div className="text-xs text-muted-foreground">{fill(p("ispAvgIn"))}</div>
          <div className="mt-1 font-head text-3xl font-800">{area.isp_avg ?? "—"} <span className="text-sm font-600 text-muted-foreground">Mbps</span></div>
          {diff != null && <div className={`mt-1 text-xs font-700 ${diff >= 0 ? "text-success" : "text-destructive"}`} data-testid="area-diff">{diff >= 0 ? "+" : ""}{diff}%</div>}
        </div>
      </div>
      {area.percentile != null && (
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-amber-500/10 px-4 py-3 text-sm font-600 text-amber-700 dark:text-amber-400" data-testid="area-percentile">
          <Trophy className="h-4 w-4" /> {fill(p("fasterThan"))}
        </div>
      )}
      {area.best_alt && (
        <div className="mt-4 text-sm" data-testid="area-best-alt">
          <span className="text-muted-foreground">{p("bestAlt")}:</span> <span className="font-700">{area.best_alt.isp}</span> · {area.best_alt.download} Mbps · {area.best_alt.ping} ms
        </div>
      )}
      {area.country_code && (
        <Link to={lp(`/city/${area.country_code.toLowerCase()}/${area.city.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ł/g, "l").replace(/[^a-z0-9]+/g, "-")}`)} className="mt-4 inline-flex items-center gap-1 text-sm font-600 text-primary" data-testid="area-city-link">
          {p("seeCity")} <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </div>
  );
}
